import { useMemo } from 'react';
import styled from '@emotion/styled';
import Text from '@molecules/Text';

import { Box, Flex } from '@chakra-ui/react';
import Button from '@molecules/Button';
import { useModal } from '@molecules/BEOffScreen';
import ModalAmenities from '@view/components/Modals/Amenities';
import AmenityComponent from '../Amenity';
import { Amenity } from '@data/types/common';
import {
  amenityCategories,
  amenityCategoryMap,
  AmenityCategoryKeys,
} from '@molecules/Amenity/icons.types';
import { useTranslation } from 'react-i18next';

const StyledAmenitiesGrid = styled.div<{ rows: number }>`
	position: relative;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
	grid-template-rows: repeat(${({ rows }) => rows}, auto) repeat(100, 0px);
	overflow: hidden;
	gap: 0px 12px;
`;

type AmenityListProps = {
  amenities: Amenity[];
  additionalAmenities?: Amenity[];
  type?: 'grid' | 'category';
  rows?: number;
  viewAll?: boolean;
};

const AmenityList = ({
  amenities = [],
  additionalAmenities = [],
  type = 'grid',
  rows = 0,
  viewAll = false,
}: AmenityListProps) => {
  const modal = useModal(ModalAmenities, { width: 560 });
  const { t } = useTranslation();

  const amenitiesByCategory = useMemo(() => {
    if (type !== 'category') {
      return [];
    }

    return [
      ...[...amenities, ...additionalAmenities]
        .reduce((acc, amenity) => {
          const { slug } = amenity;
          if (slug) {
            const category = amenityCategoryMap[slug] || 'additional-amenity';
            if (!acc.has(category)) {
              acc.set(category, []);
            }
            acc.get(category)?.push(amenity);
          } else {
            if (!acc.has('additional-amenity')) {
              acc.set('additional-amenity', []);
            }
            acc.get('additional-amenity')?.push(amenity);
          }
          return acc;
        }, new Map<AmenityCategoryKeys, Amenity[]>())
        .entries(),
    ].sort(
      (a, b) =>
        amenityCategories.indexOf(a[0]) - amenityCategories.indexOf(b[0])
    );
  }, [type, amenities, additionalAmenities]);

  return (
    <>
      {type === 'category' ? (
        <Flex direction="column" gap="24px">
          {amenitiesByCategory.map(([category, amenitiesInCategory]) => (
            <div key={category}>
              <Text type="h3" weight="600">
                {t(`amenity-category-${category}`)}
              </Text>
              {amenitiesInCategory.map(({ name, slug }, itemIndex) => (
                <AmenityComponent key={itemIndex} name={name} slug={slug} />
              ))}
            </div>
          ))}
        </Flex>
      ) : (
        <>
          <StyledAmenitiesGrid rows={rows}>
            {[...amenities, ...additionalAmenities].map(
              ({ name, slug }, itemIndex) => (
                <AmenityComponent key={itemIndex} name={name} slug={slug} />
              )
            )}
          </StyledAmenitiesGrid>
          {viewAll && (
            <Box>
              <Button
                color="primary"
                variant="outline"
                onClick={() => modal.open({ amenities, additionalAmenities })}
              >{`${t('view-all-button')} (${
                amenities.length + additionalAmenities.length
              })`}</Button>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default AmenityList;
